(function () {
  'use strict';
  if (customElements.get('platmart-card-preview')) return;

  const cache = new Map();

  function shopifyImgUrl(url, width) {
    const base = url
      .replace(/&width=\d+/, '')
      .replace(/\?width=\d+/, '')
      .replace(/[?&]$/, '');
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}width=${width}`;
  }

  function buildSrcset(url) {
    return [360, 540, 720, 900, 1080, 1296, 1512, 1728]
      .map(w => `${shopifyImgUrl(url, w)} ${w}w`)
      .join(', ');
  }

  function shouldPrefetch() {
    const conn = navigator.connection;
    if (!conn) return true;
    if (conn.saveData) return false;
    return !['slow-2g', '2g'].includes(conn.effectiveType);
  }

  const prefetchObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      prefetchObserver.unobserve(entry.target);
      const cb = () => entry.target.prefetch();
      'requestIdleCallback' in window
        ? requestIdleCallback(cb, { timeout: 3000 })
        : setTimeout(cb, 200);
    });
  }, { rootMargin: '200px' });

  customElements.define('platmart-card-preview', class extends HTMLElement {
    constructor() {
      super();
      this.productBlock = this.closest('[data-product-block]');
      if (!this.productBlock) return;

      this.mainImage    = this.productBlock.querySelector('[data-main-image] img');
      this.productLinks = this.productBlock.querySelectorAll('[data-grid-link]');
      this.titleEl      = this.productBlock.querySelector('.product-grid-item__title');
      this.priceEl      = this.productBlock.querySelector('.product-grid-item__price');
      this.badgeEl      = this.productBlock.querySelector('.product-badge__item--custom span');

      // Read the src width the server chose — reuse it for hover swaps
      this.originalSrcWidth = parseInt(
        this.mainImage?.getAttribute('src')?.match(/[?&]width=(\d+)/)?.[1]
      ) || 800;

      this.original = {
        src:       this.mainImage?.getAttribute('src'),
        srcset:    this.mainImage?.getAttribute('srcset'),
        title:     this.titleEl?.textContent,
        priceHtml: this.priceEl?.innerHTML,
        badge:     this.badgeEl?.textContent,
        url:       this.productLinks[0]?.getAttribute('href'),
      };

      this.activeHandle = null;
      this.leaveTimer = null;
      this.originalActiveSwatch = this.querySelector('.platmart-color-swatch.platmart-product-swatch-active');
      this.hoveredSwatch = null;
    }

    connectedCallback() {
      if (!this.productBlock) return;
      this.querySelectorAll('.platmart-color-swatch:not(.platmart-product-swatch-active)').forEach(swatch => {
        swatch.addEventListener('mouseenter', () => this.onEnter(swatch));
        swatch.addEventListener('mouseleave', () => {
          this.leaveTimer = setTimeout(() => this.onLeave(), 100);
        });
      });
      prefetchObserver.observe(this);
      this.dispatchEvent(new CustomEvent('theme:swatches:loaded', { bubbles: true }));
    }

    async prefetch() {
      if (!shouldPrefetch()) return;
      const handles = [...this.querySelectorAll('.platmart-color-swatch:not(.platmart-product-swatch-active)')]
        .map(s => s.dataset.hoverHandle)
        .filter(Boolean);

      for (const handle of handles) {
        if (cache.has(handle)) continue;
        try {
          const res = await fetch(
            `/products/${handle}?section_id=api-platmart-card-hover`,
            { priority: 'low' }
          );
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const data = JSON.parse(
            doc.querySelector('script[type="application/json"]').textContent
          );
          cache.set(handle, data);
          // Warm image cache at the exact width the card src uses
          if (data.image) new Image().src = shopifyImgUrl(data.image, this.originalSrcWidth);
        } catch {
          // silently skip
        }
      }
    }

    async onEnter(swatch) {
      clearTimeout(this.leaveTimer);
      const prevActive = this.hoveredSwatch || this.originalActiveSwatch;
      if (prevActive) prevActive.classList.replace('platmart-product-swatch-active', 'platmart-product-swatch-inactive');
      swatch.classList.replace('platmart-product-swatch-inactive', 'platmart-product-swatch-active');
      this.hoveredSwatch = swatch;

      const handle = swatch.dataset.hoverHandle;
      if (!handle) return;
      this.activeHandle = handle;

      let data = cache.get(handle);
      if (!data) {
        try {
          const res = await fetch(`/products/${handle}?section_id=api-platmart-card-hover`);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          data = JSON.parse(doc.querySelector('script[type="application/json"]').textContent);
          cache.set(handle, data);
          // Warm image cache before applying, same as prefetch
          if (data.image) await new Promise(resolve => {
            const img = new Image();
            img.onload = img.onerror = resolve;
            img.src = shopifyImgUrl(data.image, this.originalSrcWidth);
          });
        } catch {
          return;
        }
      }

      if (this.activeHandle !== handle) return;
      this.apply(data);
    }

    apply(data) {
      if (this.mainImage && data.image) {
        this.mainImage.setAttribute('src', shopifyImgUrl(data.image, this.originalSrcWidth));
        this.mainImage.setAttribute('srcset', buildSrcset(data.image));
      }
      if (this.titleEl && data.title)     this.titleEl.textContent = data.title;
      if (this.priceEl && data.priceHtml) this.priceEl.innerHTML = data.priceHtml;
      if (this.badgeEl)                   this.badgeEl.textContent = data.badge || '';
      this.productLinks.forEach(link => link.setAttribute('href', data.url));
    }

    onLeave() {
      this.activeHandle = null;
      if (this.hoveredSwatch) {
        this.hoveredSwatch.classList.replace('platmart-product-swatch-active', 'platmart-product-swatch-inactive');
        this.hoveredSwatch = null;
      }
      if (this.originalActiveSwatch) {
        this.originalActiveSwatch.classList.replace('platmart-product-swatch-inactive', 'platmart-product-swatch-active');
      }
      const o = this.original;
      if (this.mainImage) {
        this.mainImage.setAttribute('src', o.src);
        this.mainImage.setAttribute('srcset', o.srcset);
      }
      if (this.titleEl)  this.titleEl.textContent  = o.title;
      if (this.priceEl)  this.priceEl.innerHTML    = o.priceHtml;
      if (this.badgeEl)  this.badgeEl.textContent  = o.badge;
      this.productLinks.forEach(link => link.setAttribute('href', o.url));
    }
  });
})();
